import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import {
  createElement,
  isValidElement,
  useRef,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Kbd } from '@/components/ui/Kbd';
import { popoverSurfaceClass } from '@/components/ui/Popover';
import { parseShortcut } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

/**
 * Menu — the single dropdown-menu vocabulary for the app.
 *
 * Supports icons, right-aligned shortcut hints, separators, section labels, checkbox and
 * radio items, nested submenus and a danger variant. Radix handles the roving focus,
 * typeahead, Esc, outside-click and focus restoration.
 */

/** An icon may be a component (preferred — we own the sizing) or a ready-made node. */
export type MenuIcon = ComponentType<{ className?: string }> | ReactNode;

interface MenuItemBase {
  id: string;
  disabled?: boolean;
}

export interface MenuActionItem extends MenuItemBase {
  type?: 'item';
  label: ReactNode;
  /** Second line under the label — use sparingly. */
  description?: ReactNode;
  icon?: MenuIcon;
  shortcut?: string | string[];
  danger?: boolean;
  onSelect?: () => void;
  submenu?: MenuItem[];
  /** Extra classes on the submenu surface — a height cap for a long roster. */
  submenuClassName?: string;
  /** Trailing slot (a count, a badge). Ignored when `shortcut` is set. */
  trailing?: ReactNode;
  /** Default true. */
  closeOnSelect?: boolean;
}

/**
 * A filter field pinned inside a menu — for a section whose item list grows with
 * the workspace (people, tags, views). Character keys are kept away from Radix's
 * typeahead so they reach the input; ↓ hands focus to the first item, and Esc
 * still dismisses the menu.
 */
export interface MenuSearchItem extends MenuItemBase {
  type: 'search';
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  'aria-label'?: string;
}

export interface MenuSeparatorItem extends MenuItemBase {
  type: 'separator';
}

export interface MenuLabelItem extends MenuItemBase {
  type: 'label';
  label: ReactNode;
}

export interface MenuCheckboxItem extends MenuItemBase {
  type: 'checkbox';
  label: ReactNode;
  /**
   * Second line under the label. Used where a *disabled* toggle owes the reader
   * a reason — a column the list is sorted by cannot be hidden, and the item
   * says so rather than being greyed out in silence.
   */
  description?: ReactNode;
  icon?: MenuIcon;
  shortcut?: string | string[];
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Default false — toggling keeps the menu open. */
  closeOnSelect?: boolean;
}

export interface MenuRadioItem extends MenuItemBase {
  type: 'radio';
  label: ReactNode;
  icon?: MenuIcon;
  shortcut?: string | string[];
  checked: boolean;
  onSelect: () => void;
  /** Default true — picking a value dismisses. */
  closeOnSelect?: boolean;
}

export type MenuItem =
  | MenuActionItem
  | MenuSeparatorItem
  | MenuLabelItem
  | MenuCheckboxItem
  | MenuRadioItem
  | MenuSearchItem;

export interface MenuProps {
  items: MenuItem[];
  /** The control that opens the menu. Must forward a ref. */
  trigger: ReactElement;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Fixed width; omit to size to content (capped at 320px). */
  width?: number | string;
  className?: string;
  /** Accessible name for the menu surface. */
  label?: string;
  modal?: boolean;
}

export function Menu({
  items,
  trigger,
  align = 'start',
  side = 'bottom',
  sideOffset = 6,
  open,
  onOpenChange,
  width,
  className,
  label,
  modal = false,
}: MenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <DropdownMenu.Trigger asChild ref={triggerRef}>
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={sideOffset}
          collisionPadding={8}
          aria-label={label}
          style={width === undefined ? undefined : { width }}
          className={cn(menuSurfaceClass, className)}
          /*
            A menu's clicks belong to the menu. The content renders in a portal —
            outside this subtree in the DOM, but still inside it in the React
            tree — so an item's click bubbled all the way back to whatever the
            trigger was sitting in. Picking a stage from an inbox row's stage
            menu changed the stage AND opened the record behind it, by mouse and
            by keyboard alike; the trigger's own `stopPropagation` could not see
            it, because the click never passed through the trigger. `onSelect`
            has already run by the time this fires — bubbling order is item then
            content — so nothing an item does is lost.
          */
          onClick={(event) => event.stopPropagation()}
          /*
            A ROW MENU HAS TO GIVE FOCUS BACK TO ITS ROW.

            Radix returns focus to the trigger on close, which is right
            everywhere except the one place this component is mounted most: a
            grid's trailing action rail. That rail only paints while the row is
            hovered or holds focus, and the moment the menu opens the focus is in
            the portal. While it was drawn with `visibility: hidden` the rail was
            therefore gone by the time the menu closed and `trigger.focus()` was
            a silent no-op: Escape left `document.activeElement` on <body> and
            the next Tab landed on "Skip to content" at the top of the document —
            the reader lost their place in a 319-row list.

            So the gridcell that owns the trigger takes focus back — ALWAYS, not
            only when the trigger is unreachable. Restoring the trigger first was
            the same dead end one layer down: the rail is `opacity-0` +
            `pointer-events-none` now rather than `visibility: hidden`, so
            `trigger.focus()` succeeds, and the reader was parked on a
            `tabIndex=-1` button that the grid's roving handler cannot drive.
            Measured on /contacts, /companies and /inbox: from there ArrowLeft
            did nothing, ArrowUp/ArrowDown/Space REOPENED the menu (Radix's
            menu-button key contract owns those three on its own trigger), a
            second Escape did nothing, and Tab left for "Skip to content" at the
            top of the document.

            The cell is the only node that can carry the contract: it restores
            `:focus-within` (which brings the rail back), it is `role=gridcell`
            with the row and column indices the grid navigates by, and every key
            the grid binds still works from it. Held here rather than at the four
            call sites, because every row menu in the product owes the same
            contract; only the Inbox had it.
          */
          onCloseAutoFocus={(event) => {
            const trigger = triggerRef.current;
            const cell = trigger?.closest<HTMLElement>('[role="gridcell"]');
            if (!cell) return;
            /* The row may have been re-rendered — or recycled by the
               virtualiser — while the menu was open, in which case these two
               nodes are DETACHED and focusing them is the same silent no-op the
               hidden rail was. Re-resolve the cell out of the live document by
               its row and column indices before giving anything focus. */
            const rowIndex = cell.closest<HTMLElement>('[data-row-index]')?.dataset.rowIndex;
            const colIndex = cell.dataset.colIndex;
            const live = cell.isConnected
              ? cell
              : rowIndex !== undefined && colIndex !== undefined
                ? document.querySelector<HTMLElement>(
                    `[data-row-index="${rowIndex}"] [data-col-index="${colIndex}"]`,
                  )
                : null;
            if (!live) return;
            event.preventDefault();
            live.focus({ preventScroll: true });
          }}
        >
          <MenuItems items={items} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export const menuSurfaceClass = cn(
  popoverSurfaceClass,
  'z-[var(--z-dropdown)] min-w-[180px] max-w-[320px] p-1',
  'max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-x-hidden overflow-y-auto',
);

/** Renders a `MenuItem[]` inside an existing Radix menu content. Exported for ContextMenu. */
export function MenuItems({ items }: { items: MenuItem[] }) {
  const reserveIndicator = items.some((i) => i.type === 'checkbox' || i.type === 'radio');
  return (
    <>
      {items.map((item) => (
        <MenuNode key={item.id} item={item} reserveIndicator={reserveIndicator} />
      ))}
    </>
  );
}

function MenuNode({ item, reserveIndicator }: { item: MenuItem; reserveIndicator: boolean }) {
  if (item.type === 'separator') {
    return <DropdownMenu.Separator className="my-1 h-px bg-[var(--line-subtle)]" />;
  }

  if (item.type === 'label') {
    return (
      <DropdownMenu.Label className="eyebrow flex h-[var(--h-control-sm)] items-center px-2 pt-1">
        {item.label}
      </DropdownMenu.Label>
    );
  }

  if (item.type === 'search') {
    return (
      <div
        className="sticky top-0 z-10 bg-[var(--surface-overlay)] px-1 pt-0.5 pb-1"
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            const menu = event.currentTarget.closest('[role="menu"]');
            menu?.querySelector<HTMLElement>('[role^="menuitem"]:not([data-disabled])')?.focus();
            return;
          }
          if (event.key === 'Escape' || event.key === 'Tab') return;
          event.stopPropagation();
        }}
      >
        <input
          type="text"
          value={item.value}
          disabled={item.disabled}
          onChange={(event) => item.onValueChange(event.target.value)}
          placeholder={item.placeholder}
          aria-label={item['aria-label'] ?? item.placeholder ?? 'Filter'}
          className={cn(
            'h-[var(--h-control-sm)] w-full rounded-[var(--radius-sm)] px-2 text-base',
            'border border-line bg-[var(--surface-inset)] text-primary outline-none',
            'placeholder:text-muted focus-visible:border-[var(--focus)]',
          )}
        />
      </div>
    );
  }

  if (item.type === 'checkbox') {
    return (
      <DropdownMenu.CheckboxItem
        checked={item.checked}
        onCheckedChange={item.onCheckedChange}
        disabled={item.disabled}
        onSelect={(event) => {
          if (!item.closeOnSelect) event.preventDefault();
        }}
        className={cn(itemClass(false), item.description && 'py-1')}
      >
        {/* `--text-primary`, not `--accent-text`. A tick is an "on" state on a
            menu row, and gold is spent on create and nothing else — the mark's
            PRESENCE is what says the row is on, the same way the sort arrow's
            presence says the column is sorted. Painted gold, the Columns menu
            put eight brand marks on screen at once. */}
        <span className="flex size-4 shrink-0 items-center justify-center text-primary">
          <DropdownMenu.ItemIndicator>
            <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
          </DropdownMenu.ItemIndicator>
        </span>
        <MenuIconSlot icon={item.icon} />
        <span className="min-w-0 flex-1">
          <span className="block truncate">{item.label}</span>
          {item.description ? (
            <span className="mt-0.5 block truncate text-xs text-muted">{item.description}</span>
          ) : null}
        </span>
        <MenuShortcut shortcut={item.shortcut} />
      </DropdownMenu.CheckboxItem>
    );
  }

  if (item.type === 'radio') {
    return (
      <DropdownMenu.Item
        role="menuitemradio"
        aria-checked={item.checked}
        disabled={item.disabled}
        onSelect={(event) => {
          if (item.closeOnSelect === false) event.preventDefault();
          item.onSelect();
        }}
        className={itemClass(false)}
      >
        <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
          <span
            className={cn(
              'size-1.5 rounded-[var(--radius-full)] transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              // `--text-primary`, for the same reason the checkbox tick above
              // takes it: the dot's presence is the state, not its hue.
              item.checked ? 'bg-[var(--text-primary)] opacity-100' : 'opacity-0',
            )}
          />
        </span>
        <MenuIconSlot icon={item.icon} />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        <MenuShortcut shortcut={item.shortcut} />
      </DropdownMenu.Item>
    );
  }

  if (item.submenu && item.submenu.length > 0) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger disabled={item.disabled} className={itemClass(item.danger)}>
          {reserveIndicator ? <span className="size-4 shrink-0" aria-hidden /> : null}
          <MenuIconSlot icon={item.icon} />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          <ChevronRight className="size-3.5 shrink-0 text-muted" aria-hidden />
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent
            sideOffset={4}
            alignOffset={-4}
            collisionPadding={8}
            className={cn(menuSurfaceClass, item.submenuClassName)}
          >
            <MenuItems items={item.submenu} />
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    );
  }

  return (
    <DropdownMenu.Item
      disabled={item.disabled}
      onSelect={(event) => {
        if (item.closeOnSelect === false) event.preventDefault();
        item.onSelect?.();
      }}
      className={cn(itemClass(item.danger), item.description && 'py-1')}
    >
      {reserveIndicator ? <span className="size-4 shrink-0" aria-hidden /> : null}
      <MenuIconSlot icon={item.icon} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{item.label}</span>
        {item.description ? (
          <span className="mt-0.5 block truncate text-xs text-muted">{item.description}</span>
        ) : null}
      </span>
      {item.shortcut ? (
        <MenuShortcut shortcut={item.shortcut} />
      ) : item.trailing ? (
        <span className="shrink-0 text-xs text-muted tnum">{item.trailing}</span>
      ) : null}
    </DropdownMenu.Item>
  );
}

function itemClass(danger?: boolean) {
  return cn(
    'group/mi relative flex min-h-[var(--h-control-sm)] cursor-default select-none items-center gap-2',
    'rounded-[var(--radius-sm)] px-2 text-base leading-snug outline-none',
    'transition-colors ease-[var(--ease-out)] duration-[var(--dur-instant)]',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
    danger
      ? 'text-[var(--danger)] data-[highlighted]:bg-[var(--danger-soft)] data-[state=open]:bg-[var(--danger-soft)]'
      : 'text-secondary data-[highlighted]:bg-[var(--surface-hover)] data-[highlighted]:text-primary data-[state=open]:bg-[var(--surface-hover)] data-[state=open]:text-primary',
  );
}

function MenuIconSlot({ icon }: { icon?: MenuIcon }) {
  if (!icon) return null;
  if (typeof icon === 'function') {
    return createElement(icon, {
      className: 'size-4 shrink-0 text-muted group-data-[highlighted]/mi:text-primary',
    });
  }
  if (isValidElement(icon)) {
    return <span className="flex size-4 shrink-0 items-center justify-center text-muted">{icon}</span>;
  }
  return null;
}

function MenuShortcut({ shortcut }: { shortcut?: string | string[] }) {
  const keys = parseShortcut(shortcut);
  if (!keys) return null;
  return (
    <span className="ml-auto shrink-0 pl-3">
      <Kbd keys={keys} />
    </span>
  );
}

export const MenuRoot = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuPortal = DropdownMenu.Portal;
export const MenuContentPrimitive = DropdownMenu.Content;
